import React, { useMemo, useCallback } from 'react';
import BodyClassName from 'react-body-classname';
import { Page } from 'components/core/Page';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { PaidTemplateHeaderImages, PPC_TYPE_STORAGE } from './constants';
import { PAGE_TYPES } from '@sparefoot/tracking-events';
import { withPageViewTracking } from 'components/core/library/tracking-utils';
import useExperiment from 'hooks/useExperiment';

import { withSearchHandler } from 'decorators';
import { PLPHeroDynamic } from 'components/plp/PLPHeroDynamic';
import { StickySearchBar } from 'components/landing/StickySearchBar';
import { WhySpareFootSection } from '../../components/plp/WhySpareFootSection';
import PLPHowItWorksSection from '../../components/plp/PLPHowItWorksSection/PLPHowItWorksSection';
import PLPReviewSection from '../../components/plp/PLPReviewSection/PLPReviewSection';
import PLPFooterSection from '../../components/plp/PLPFooterSection/PLPFooterSection';
import PLPQuickLinks from '../../components/plp/PLPQuickLinks/PLPQuickLinks';

import './PaidTemplatePage.scss';
import { PLPFacilitiesSection } from '../../components/plp/PLPFacilitiesSection';

export const enhance = (Component) =>
	withPageViewTracking(PAGE_TYPES.PPC)(withSearchHandler(Component));

export function PaidTemplatePage({ handleSearch }) {
	const storageType = useSelector((state) => state.pageMeta.storageType);
	const location = useSelector((state) => state.pageMeta.location);

	const { sf14851Variation } = useExperiment(
		'sf_14851_sfdc_paid_landing_hero_promo_test',
	);
	const { sf15342Variation } = useExperiment('sf_15342_geolocation_sfdc');
	const searchType = storageType !== PPC_TYPE_STORAGE ? 'vehicle' : 'storage';
	const additionalSearchParams =
		storageType !== PPC_TYPE_STORAGE
			? { vehicle: storageType.toLowerCase() }
			: undefined;

	const buildTitle = useCallback((loc, keyword) => {
		let appendStorageString =
			keyword !== PPC_TYPE_STORAGE ? `${keyword} Storage` : keyword;
		if (loc) {
			return `Find ${appendStorageString} in ${loc}`;
		}
		return `Find ${appendStorageString} Near You`;
	}, []);

	const getHeaderImage = useCallback((keyword) => {
		return (
			PaidTemplateHeaderImages[keyword] ||
			PaidTemplateHeaderImages['default']
		);
	}, []);

	const title = useMemo(
		() => buildTitle(location, storageType),
		[buildTitle, location, storageType],
	);
	const pageType = storageType;
	const imageSrc = useMemo(
		() => getHeaderImage(storageType),
		[getHeaderImage, storageType],
	);

	const quickLinks = [
		{ id: 'how-it-works', label: 'How It Works' },
		{ id: 'reviews-section', label: 'Reviews' },
		{ id: 'why-sparefoot', label: 'Why Sparefoot' },
	];

	return (
		<BodyClassName className="paid-template-page">
			<Page>
				<StickySearchBar
					triggerId="quick-links-container"
					handleSearch={handleSearch}
					hasJumpToSection={<PLPQuickLinks quickLinks={quickLinks} />}
					className="plp-sticky-search"
				/>
				<PLPHeroDynamic
					headline={title}
					searchButtonText="Find units"
					storageType={pageType}
					handleSearch={handleSearch}
					sf14851Variation={sf14851Variation}
				/>
				<PLPQuickLinks
					id="quick-links-container"
					quickLinks={quickLinks}
				/>
				{/* TODO: add facilities section */}
				{sf15342Variation === 'variation1' && ( // sf_15342_geolocation_sfdc
					<PLPFacilitiesSection />
				)}
				<PLPHowItWorksSection pageType={pageType} />
				<PLPReviewSection pageType={pageType} />
				<WhySpareFootSection />
				<PLPFooterSection
					pageType={pageType}
					location={location}
					handleSearch={handleSearch}
				/>
			</Page>
		</BodyClassName>
	);
}

PaidTemplatePage.propTypes = {
	storageType: PropTypes.string,
	handleSearch: PropTypes.func.isRequired,
	location: PropTypes.string,
};

export default enhance(PaidTemplatePage);
